import React, { memo, useCallback, useEffect, useState } from "react";
import Select from "react-select";
import Icon from "../../../../components/lib/Icon";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogActions,
  DialogClose,
} from "../../../../components/lib/Dialog";
import { Button } from "../../../../components/lib/Button";
import Inline from "../../../../components/lib/Inline";
import Box from "../../../../components/lib/Box";
import Text from "../../../../components/lib/Text";
import { ButtonGroupRight } from "../../../../components/lib/ButtonGroup";
import Stack from "../../../../components/lib/Stack";
// @ts-ignore
import InputBox from "../../../../components/inputs/InputBox";
import { Label } from "../../../../components/lib/Label";
import {
  useUploadUserContext,
  UniqueUser,
  type User,
} from "../context/UploadUserContext";

type EditUserProps = {
  user: UniqueUser;
  userList: UniqueUser[];
  index: number;
  keyType: string;
};

type UserDialogProps = {
  user: UniqueUser;
  show: boolean;
  onClose: () => void;
  onSubmit: (userDetails: UniqueUser) => void;
};

type ErrorState = {
  email: string | null;
  username: string | null;
  client_id: string | null;
  country_id: string | null;
};

const initialErrorState: ErrorState = {
  email: null,
  username: null,
  client_id: null,
  country_id: null,
};

const options: { value: boolean; label: string }[] = [
  { value: true, label: "True" },
  { value: false, label: "False" },
];

const UserDialog = ({ user, show, onClose, onSubmit }: UserDialogProps) => {
  const [userDetails, setUserDetails] = useState<Omit<User, "error">>({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    client_id: "",
    country_id: "",
    release_user: false,
    release_password: false,
  });

  const [error, setError] = useState<ErrorState>(initialErrorState);

  useEffect(() => {
    if (user) {
      setUserDetails(user);
    }
  }, [user]);

  const handleUpdate = useCallback(() => {
    const localError: ErrorState = {
      email: null,
      username: null,
      client_id: null,
      country_id: null,
    };

    if (!userDetails?.username || userDetails?.username.trim() === "") {
      localError.username = "Username is required";
    } else if (userDetails?.username.length < 3) {
      localError.username = "Username must be at least 3 characters long";
    }

    if (!userDetails?.email || userDetails?.email.trim() === "") {
      localError.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(userDetails?.email)) {
      localError.email = "Email is invalid";
    }

    if (!userDetails?.client_id || String(userDetails?.client_id).trim() === "") {
      localError.client_id = "Client ID is required";
    } else if (isNaN(parseInt(String(userDetails?.client_id)))) {
      localError.client_id = "Client ID must be a number";
    }

    if (!userDetails?.country_id || String(userDetails?.country_id).trim() === "") {
      localError.country_id = "Country ID is required";
    } else if (isNaN(parseInt(String(userDetails?.country_id)))) {
      localError.country_id = "Country ID must be a number";
    }

    setError(localError);

    if (Object.values(localError).some((val) => val && val.length)) {
      return;
    }
    onSubmit(userDetails as UniqueUser);
  }, [userDetails, onSubmit, setError]);

  const updateUserDetails = useCallback((key: keyof User, value: string | boolean) => {
    setUserDetails((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent css={{ minWidth: "700px" }}>
        <DialogTitle asChild>
          <Inline css={{ justifyContent: "space-between" }}>
            <Box>
              <Text as="h4">Select Payment Report Parameters</Text>
            </Box>
            <DialogClose asChild>
              <Icon icon="times" />
            </DialogClose>
          </Inline>
        </DialogTitle>
        <DialogDescription css={{ backgroundColor: "inherit" }} asChild>
          <Stack
            fill
            css={{
              alignItems: "flex-start",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <Box>
              <Label css={{ fontSize: "16px", fontWeight: "bold" }}>First Name</Label>
              <InputBox
                lable="First Name"
                value={userDetails?.first_name}
                onChange={(value: string) => updateUserDetails("first_name", value)}
                placeholder="Enter First Name"
              />
            </Box>
            <Box>
              <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Last Name</Label>
              <InputBox
                lable="Last Name"
                value={userDetails?.last_name}
                onChange={(value: string) => updateUserDetails("last_name", value)}
                placeholder="Enter Last Name"
              />
            </Box>
            <Box>
              <Box css={{ flexFlow: "row" }}>
                <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Email</Label>
                {error.email && (
                  <Text css={{ color: "red", fontSize: "12px", marginLeft: "10px" }}>
                    {error.email}
                  </Text>
                )}
              </Box>
              <InputBox
                type="email"
                lable="email"
                value={userDetails?.email}
                onChange={(value: string) => updateUserDetails("email", value)}
                placeholder="Enter Email"
                style={{
                  width: "100%",
                  borderColor: error.email ? "red" : "",
                }}
              />
            </Box>
            <Box>
              <Box css={{ flexFlow: "row" }}>
                <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Username</Label>
                {error.username && (
                  <Text css={{ color: "red", fontSize: "12px", marginLeft: "10px" }}>
                    {error.username}
                  </Text>
                )}
              </Box>
              <InputBox
                lable="username"
                value={userDetails?.username}
                onChange={(value: string) => updateUserDetails("username", value)}
                placeholder="Enter Username"
                style={{
                  width: "100%",
                  borderColor: error.username ? "red" : "",
                }}
              />
            </Box>
            <Box>
              <Box css={{ flexFlow: "row" }}>
                <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Client Id</Label>
                {error.client_id && (
                  <Text css={{ color: "red", fontSize: "12px", marginLeft: "10px" }}>
                    {error.client_id}
                  </Text>
                )}
              </Box>
              <InputBox
                lable="client Id"
                type="text"
                value={userDetails?.client_id}
                onChange={(value: string) => updateUserDetails("client_id", value)}
                placeholder="Enter Client Id"
                style={{
                  width: "100%",
                  borderColor: error.client_id ? "red" : "",
                }}
              />
            </Box>
            <Box>
              <Box css={{ flexFlow: "row" }}>
                <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Country Id</Label>
                {error.country_id && (
                  <Text css={{ color: "red", fontSize: "12px", marginLeft: "10px" }}>
                    {error.country_id}
                  </Text>
                )}
              </Box>
              <InputBox
                type="text"
                lable="country Id"
                value={userDetails?.country_id}
                onChange={(value: string) => updateUserDetails("country_id", value)}
                placeholder="Enter Country Id"
                style={{
                  width: "100%",
                  borderColor: error.country_id ? "red" : "",
                }}
              />
            </Box>
            <Box>
              <Label css={{ fontSize: "16px", fontWeight: "bold" }}>Release User</Label>
              <Select
                options={options}
                defaultValue={options[0]}
                value={options.find(
                  (option) => option.value === userDetails?.release_user
                )}
                name="release_user"
                onChange={(option: any, actionMeta: any) =>
                  updateUserDetails("release_user", option.value)
                }
                placeholder="Select Release User"
                isSearchable={false}
              />
            </Box>
            <Box>
              <Label css={{ fontSize: "16px", fontWeight: "bold" }}>
                Release Password
              </Label>
              <Select
                options={options}
                defaultValue={options[0]}
                value={options.find(
                  (option) => option.value === userDetails?.release_password
                )}
                name="release_password"
                onChange={(option: any, actionMeta: any) =>
                  updateUserDetails("release_password", option.value)
                }
                placeholder="Select Release Password"
                isSearchable={false}
              />
            </Box>
          </Stack>
        </DialogDescription>
        <DialogActions>
          <ButtonGroupRight fill reverse>
            <Button color="gray" size="normal" onClick={onClose}>
              Close
            </Button>
            <Button
              color="green"
              size="normal"
              loadingText="Updating..."
              //   disabled={!state.reportFromDate || !state.reportToDate}
              onClick={() => handleUpdate()}
            >
              Update User
            </Button>
          </ButtonGroupRight>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

const EditUser = memo(({ user, userList, index, keyType }: EditUserProps) => {
  const { updateList } = useUploadUserContext();
  const [showModal, setShowModal] = useState(false);

  const onSubmit = useCallback(
    (userDetails) => {
      const newList = userList?.map((u, i) => (i === index ? userDetails : u));
      updateList(newList, keyType);
      setShowModal(false);
    },
    [updateList, index, keyType, userList]
  );

  return (
    <>
      <Icon
        css={{ color: "$brandLight !important", cursor: "pointer" }}
        icon="pencil-alt"
        onClick={() => setShowModal(true)}
      />
      {showModal && (
        <UserDialog
          user={user}
          show={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
});

export default EditUser;
